<script setup lang="ts">
import type { CookieItem } from '~/types/content/cookieItem.type';

const cookies: Record<string, CookieItem[]> = {
    necessary: [
        {
            name: '_GRECAPTCHA',
            provider: 'google.com',
            purpose:
                "Questo cookie è usato per distinguere tra umani e robot. Questo è utile per il sito web, al fine di rendere validi rapporti sull'uso del sito.",
            expiry: '180 giorni',
            type: 'HTTP Cookie',
        },
        {
            name: 'CookieConsent',
            provider: 'consent.cookiebot.com',
            purpose: "Memorizza lo stato del consenso ai cookie dell'utente per il dominio corrente",
            expiry: '1 anno',
            type: 'HTTP Cookie',
        },
        {
            name: 'iconify-count',
            provider: 'fleequid.com',
            purpose:
                'Utilizzato dal Content Management System (CMS; in italiano "sistema di gestione dei contenuti") del sito web per determinare come debbano essere visualizzati i menù delle schede del sito.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'iconify-version',
            provider: 'fleequid.com',
            purpose:
                'Utilizzato dal Content Management System (CMS; in italiano "sistema di gestione dei contenuti") del sito web per determinare come debbano essere visualizzati i menù delle schede del sito.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::a',
            provider: 'gstatic.com',
            purpose:
                "Questo cookie è usato per distinguere tra umani e robot. Questo è utile per il sito web, al fine di rendere validi rapporti sull'uso del sito.",
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::b',
            provider: 'gstatic.com',
            purpose: 'Questo cookie è usato per distinguere tra umani e robot.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::c',
            provider: 'gstatic.com',
            purpose: 'Questo cookie è usato per distinguere tra umani e robot.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::f',
            provider: 'gstatic.com',
            purpose: 'Questo cookie è usato per distinguere tra umani e robot.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
    ],
    preferences: [
        {
            name: 'i18n_redirected',
            provider: 'fleequid.com',
            purpose: 'Determina la lingua preferita del visitatore. Consente al sito web di impostare la lingua preferita al rientro del visitatore.',
            expiry: '1 anno',
            type: 'HTTP Cookie',
        },
    ],
    marketing: [
        {
            name: '_ga',
            provider: 'www.googletagmanager.com',
            purpose:
                "Utilizzato per inviare dati a Google Analytics in merito al dispositivo e al comportamento dell'utente. Tiene traccia dell'utente su dispositivi e canali di marketing.",
            expiry: '2 anni',
            type: 'HTTP Cookie',
        },
        {
            name: '_ga#',
            provider: 'www.googletagmanager.com',
            purpose:
                "Utilizzato per inviare dati a Google Analytics in merito al dispositivo e al comportamento dell'utente. Tiene traccia dell'utente su dispositivi e canali di marketing.",
            expiry: '2 anni',
            type: 'HTTP Cookie',
        },
    ],
    unclassified: [
        {
            name: 'nuxt-color-mode',
            provider: 'fleequid.com',
            purpose: 'Determina il tema preferito del visitatore.',
            expiry: 'Persistent',
            type: 'HTML Local Storage',
        },
    ],
};
</script>
<template>
    <ContentPage title="Cookie Policy">
        <p>
            Utilizziamo i cookie per personalizzare contenuti ed annunci, per fornire funzionalità dei social media e per analizzare il nostro
            traffico. Condividiamo inoltre informazioni sul modo in cui utilizzi il nostro sito con i nostri partner che si occupano di analisi dei
            dati web, pubblicità e social media, i quali potrebbero combinarle con altre
        </p>
        <p>I cookie sono piccoli file di testo che possono essere utilizzati dai siti web per rendere più efficiente l'esperienza per l'utente.</p>
        <p>
            La legge afferma che possiamo memorizzare i cookie sul tuo dispositivo se sono strettamente necessari per il funzionamento di questo sito.
            Per tutti gli altri tipi di cookie abbiamo bisogno del tuo consenso.
        </p>
        <p>Questo sito utilizza diversi tipi di cookie. Alcuni cookie sono collocati da servizi di terzi che compaiono sulle nostre pagine.</p>
        <p>In qualsiasi momento è possibile modificare o revocare il proprio consenso dalla Dichiarazione dei cookie sul nostro sito web.</p>
        <p>
            Scopri di più su chi siamo, come puoi contattarci e come trattiamo i dati personali nella nostra sulla
            <NuxtLinkLocale to="privacy-policy" class="text-primary-500 hover:underline">Informativa privacy</NuxtLinkLocale>.
        </p>
        <p>Specifica l’ID del tuo consenso e la data di quando ci hai contattati per quanto riguarda il tuo consenso.</p>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Necessari (8)</h3>
            <p>
                I cookie necessari contribuiscono a rendere fruibile il sito web abilitandone funzionalità di base quali la navigazione sulle pagine e
                l'accesso alle aree protette del sito. Il sito web non è in grado di funzionare correttamente senza questi cookie.
            </p>
            <ContentCookieTable :items="cookies.necessary" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Preferenze (1)</h3>
            <p>
                I cookie di preferenza consentono al sito web di memorizzare informazioni che ne influenzano il comportamento o l'aspetto, quali la
                lingua preferita o la località nella quale ti trovi.
            </p>
            <ContentCookieTable :items="cookies.preferences" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Marketing (2)</h3>
            <p>
                I cookie di marketing vengono utilizzati per tracciare i visitatori sui siti web. La finalità è quella di presentare annunci
                pubblicitari che siano rilevanti e coinvolgenti per il singolo utente e quindi di maggior valore per editori e inserzionisti di terze
                parti.
            </p>
            <ContentCookieTable :items="cookies.marketing" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Non classificati (1)</h3>
            <p>I cookie non classificati sono i cookie che sono in fase di classificazione, insieme ai fornitori di cookie individuali.</p>
            <ContentCookieTable :items="cookies.unclassified" />
        </div>
    </ContentPage>
</template>
